// Lightweight fetcher for ``GET /public-config`` — the operator-set
// dashboard config that has to render correctly even on error pages
// (where the user has no session).
//
// Cached in-module on first success.  We don't cache failures so a
// transient network error doesn't permanently degrade the footer to
// the hard-coded fallback.

import { useEffect, useState } from "react";

export interface PublicConfig {
    support_email: string;
    signups_enabled: boolean;
    signup_mode: "public" | "invite_only";
    email_delivery_enabled: boolean;
    isolated_runtime_enabled: boolean;
    oauth_providers: string[];
    dashboard_base_url: string;
    /** PostHog public project key — empty disables telemetry. */
    posthog_api_key: string;
    /** PostHog ingestion host (defaults to US; EU/self-hosted overridable). */
    posthog_host: string;
    /** Whether Stripe billing is configured on the backend.  When
     * false the dashboard hides upgrade CTAs so the user doesn't
     * click into a 503. */
    billing_enabled: boolean;
    billing_provider?: "stripe" | "paddle";
    paddle_environment?: "sandbox" | "live";
    paddle_client_token?: string;
    /** External status-page URL (Statuspage, Instatus, Upptime, etc.).
     * Empty hides the footer link rather than rendering a dead URL. */
    status_page_url: string;
}

// Sane fallback used when the backend is unreachable — keeps the UI
// rendering with a generic mailto: link rather than a broken layout.
// Operators with a custom support inbox will see this only briefly,
// before the real ``/public-config`` fetch lands.
const FALLBACK: PublicConfig = {
    support_email: "support@pugio.co.za",
    signups_enabled: false,
    signup_mode: "invite_only",
    email_delivery_enabled: false,
    isolated_runtime_enabled: false,
    oauth_providers: [],
    dashboard_base_url: typeof window !== "undefined" ? window.location.origin : "",
    posthog_api_key: "",
    posthog_host: "https://us.i.posthog.com",
    billing_enabled: false,
    status_page_url: "",
};

let _cache: PublicConfig | null = null;
let _inflight: Promise<PublicConfig> | null = null;

export async function fetchPublicConfig(): Promise<PublicConfig> {
    if (_cache) return _cache;
    if (_inflight) return _inflight;
    _inflight = (async () => {
        try {
            const res = await fetch("/api/public-config", {
                headers: { Accept: "application/json" },
                cache: "no-store",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = (await res.json()) as PublicConfig;
            _cache = data;
            return data;
        } catch {
            // Don't cache on failure — try again next time.
            return FALLBACK;
        } finally {
            _inflight = null;
        }
    })();
    return _inflight;
}

/** React hook: returns the public config, the fallback while the
 * fetch is in flight, and a stable reference once cached. */
export function usePublicConfig(): PublicConfig {
    const [cfg, setCfg] = useState<PublicConfig>(_cache ?? FALLBACK);
    useEffect(() => {
        let live = true;
        fetchPublicConfig().then((v) => { if (live) setCfg(v); });
        return () => { live = false; };
    }, []);
    return cfg;
}
